import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AccountBar from './account/AccountBar';
import BreadcrumbBar, { type BreadcrumbAction } from './BreadcrumbBar';
import AccountMetrics from './account/AccountMetrics';
import ChooseProjectDialog from './account/ChooseProjectDialog';
import { vendorMetrics } from './account/accountMetricSets';
import { useExport } from './header/exporters';
import { roleLevel } from '../utils/roles';
import LoadErrorBox from './ui/LoadErrorBox';
import idColumn from './ui/idColumn';
import DataTable, { type Column } from './DataTable';
import EmptyState from './EmptyState';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from './NotificationProvider';
import { isArchiveView, toArchiveView, type ArchiveView } from '../utils/archiveFilter';
import { errorMessageOf } from '../utils/apiErrors';
import { entityPath, NEW_VENDOR_PATH } from '../utils/entityRoutes';
import { getActiveProjectId } from '../utils/session';
import { openInProject } from '../utils/openInProject';
import { loadAccountVendors, type AccountVendor } from '../services/AccountVendorService';
import '../styles/components/VendorDirectoryView.css';

/** "pending_review" -> "Pending review". */
export const statusLabel = (status: string | null): string => {
  if (!status) return 'No status';
  const spaced = status.replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const statusClass = (status: string | null): string => `vd-status vd-status--${(status ?? 'none').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

const distinct = (values: Array<string | null>): string[] =>
  Array.from(new Set(values.filter((v): v is string => !!v))).sort((a, b) => a.localeCompare(b));

const ratingText = (v: AccountVendor): string =>
  v.rating === null ? 'Not rated' : `${v.rating.toFixed(1)} (${v.ratingCount})`;

/**
 * Account-level Vendor Directory: the vendors of every project in one searchable,
 * sortable table. Vendors are added inside a project; this page only reads them.
 * Opening one switches into that vendor's project first, since a vendor page
 * belongs to its project.
 */
const VendorDirectoryView: React.FC = () => {
  const navigate = useNavigate();
  const { switchWorkspace, workspaces } = useAuth();
  const { showError } = useNotifications();

  const [vendors, setVendors] = useState<AccountVendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [archiveFilter, setArchiveFilter] = useState<ArchiveView>('active');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [opening, setOpening] = useState<string | null>(null);
  const [choosingProject, setChoosingProject] = useState(false);
  const [startingNew, setStartingNew] = useState(false);

  const reload = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    loadAccountVendors(isArchiveView(archiveFilter))
      .then(rows => { if (!cancelled) setVendors(rows); })
      .catch(err => { if (!cancelled) setLoadError(err ?? new Error('load failed')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [archiveFilter]);

  useEffect(() => reload(), [reload]);

  // The archive switch decides which records exist; the server may send both.
  const inView = useMemo(
    () => vendors.filter(v => v.archived === isArchiveView(archiveFilter)),
    [vendors, archiveFilter],
  );
  const categories = useMemo(() => distinct(inView.flatMap(v => [v.category, ...v.categories])), [inView]);
  const statuses = useMemo(() => distinct(inView.map(v => v.status)), [inView]);

  const visible = useMemo(
    () => inView.filter(v =>
      (!category || v.category === category || v.categories.includes(category)) &&
      (!status || v.status === status)),
    [inView, category, status],
  );

  // Export button: the vendors listed right now.
  // Nothing while a load is under way or has failed, so a stale view is never exported.
  useExport('account-vendors', { rows: loading || loadError ? [] : visible, ctx: undefined });

  // A vendor belongs to a project, so New vendor asks which one (only projects this person can edit),
  // then opens that project's vendor form, switching into the project first when it is another one.
  const editableProjects = useMemo(
    () => (workspaces ?? []).filter(w => roleLevel(w.role) >= 2),
    [workspaces],
  );
  const startNewVendor = async (projectId: string) => {
    if (startingNew) return;
    setStartingNew(true);
    try {
      await openInProject({ target: NEW_VENDOR_PATH, projectId, activeProjectId: getActiveProjectId(), navigate, switchWorkspace });
      setChoosingProject(false);
    } catch (err) {
      showError(errorMessageOf(err, "Couldn't open that project. Please try again."));
    } finally {
      setStartingNew(false);
    }
  };

  const openVendor = async (vendor: AccountVendor) => {
    const target = entityPath('vendor', vendor.id);
    if (!target || opening) return;
    const otherProject = !!vendor.projectId && vendor.projectId !== getActiveProjectId();
    if (otherProject) setOpening(vendor.id);
    try {
      await openInProject({ target, projectId: vendor.projectId, activeProjectId: getActiveProjectId(), navigate, switchWorkspace });
    } catch (err) {
      showError(errorMessageOf(err, "Couldn't open that project. Please try again."));
      setOpening(null);
    }
  };

  const columns: Column<AccountVendor>[] = [
    idColumn<AccountVendor>('vendor'),
    {
      key: 'name',
      header: 'Vendor',
      sortable: true,
      render: (v, row) => (
        <button type="button" className="vd-name" onClick={e => { e.stopPropagation(); void openVendor(row); }} disabled={opening !== null}>
          {v}
        </button>
      ),
    },
    { key: 'category', header: 'Category', sortable: true, render: v => v || '—' },
    { key: 'status', header: 'Status', sortable: true, render: v => <span className={statusClass(v)}>{statusLabel(v)}</span> },
    { key: 'rating', header: 'Rating', sortable: true, align: 'right', sortValue: row => row.rating ?? -1, render: (_v, row) => ratingText(row) },
    { key: 'projectName', header: 'Project', sortable: true, className: 'vd-project' },
  ];

  const barActions: BreadcrumbAction[] = [
    {
      kind: 'filters', key: 'filters', visible: categories.length > 0 || statuses.length > 0,
      selects: [
        ...(categories.length > 0 ? [{
          key: 'category', label: 'Category', value: category, defaultValue: '', onChange: setCategory,
          options: [{ value: '', label: 'All categories' }, ...categories.map(c => ({ value: c, label: c }))],
        }] : []),
        ...(statuses.length > 0 ? [{
          key: 'status', label: 'Status', value: status, defaultValue: '', onChange: setStatus,
          options: [{ value: '', label: 'All statuses' }, ...statuses.map(st => ({ value: st, label: statusLabel(st) }))],
        }] : []),
      ],
    },
    ...(editableProjects.length > 0
      ? [{ kind: 'primary' as const, key: 'new', icon: 'plus', label: 'New vendor', onClick: () => setChoosingProject(true) }]
      : []),
  ];

  const archived = isArchiveView(archiveFilter);

  return (
    <div className="wt-container fade-in vd-page">
      <AccountBar
        view="vendor-directory"
        archived={{ checked: archived, onChange: on => { setArchiveFilter(toArchiveView(on)); setCategory(''); setStatus(''); } }}
        actions={<BreadcrumbBar actions={barActions} />}
        exportKey="account-vendors"
      />

      {!archived && !loadError && !loading && inView.length > 0 && (
        <AccountMetrics label="Vendors summary" metrics={vendorMetrics(visible)} />
      )}

      {loadError ? (
        <LoadErrorBox what="vendors" error={loadError} onRetry={reload} retrying={loading} />
      ) : !loading && inView.length === 0 ? (
        <EmptyState
          icon="briefcase"
          title={archived ? 'No archived vendors' : 'No vendors yet'}
          description={archived
            ? 'Vendors you archive inside a project show up here.'
            : 'Vendors are added inside a project, under Vendors. Once a project has vendors they are listed here with the project they belong to.'}
        />
      ) : (
        <DataTable<AccountVendor>
          recordList={{ kind: 'vendor' }}
          data={visible}
          columns={columns}
          loading={loading}
          hoverable
          defaultSort={{ key: 'name', direction: 'asc' }}
          onRowClick={row => { void openVendor(row); }}
          emptyMessage="No vendors match your search or filters."
        />
      )}

      <ChooseProjectDialog
        open={choosingProject}
        title="New vendor"
        what="vendor"
        projects={editableProjects}
        activeProjectId={getActiveProjectId()}
        busy={startingNew}
        onClose={() => setChoosingProject(false)}
        onContinue={projectId => { void startNewVendor(projectId); }}
      />
    </div>
  );
};

export default VendorDirectoryView;
